import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with global error handling
// Helper to safely extract error message from API responses
const getErrorMessage = (data, fallback) => {
  const detail = data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  if (typeof detail === 'object') return JSON.stringify(detail);
  return fallback;
};

// Export for use in components
export { getErrorMessage };

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, message } = error;

    // Callers that handle their own fallback (e.g. live avatar) opt out of global toasts
    if (error.config?.silent) {
      return Promise.reject(error);
    }
    
    // Network error (no response)
    if (!response) {
      toast({
        title: "Connection Error",
        description: "Unable to connect to server. Please check your internet connection.",
        variant: "destructive",
      });
      return Promise.reject(error);
    }

    const { status, data } = response;
    
    // Sanitize detail: convert object/array to string so downstream catch blocks never crash React
    if (data && data.detail && typeof data.detail !== 'string') {
      if (Array.isArray(data.detail)) {
        data.detail = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (typeof data.detail === 'object') {
        data.detail = JSON.stringify(data.detail);
      }
    }
    
    switch (status) {
      case 401:
        // Don't redirect for verify-code (2FA flow) or login attempts
        if (error.config?.url?.includes('/auth/verify-code') || error.config?.url?.includes('/auth/login')) {
          break;
        }
        // Unauthorized - clear auth and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast({
          title: "Session Expired",
          description: "Please log in again to continue.",
          variant: "destructive",
        });
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        break;
        
      case 403:
        toast({
          title: "Access Denied",
          description: getErrorMessage(data, "You don't have permission to perform this action."),
          variant: "destructive",
        });
        break;
        
      case 404:
        // Don't show toast for 404s - let components handle them
        break;
        
      case 422:
        // Validation error
        toast({
          title: "Validation Error",
          description: getErrorMessage(data, "Please check your input and try again."),
          variant: "destructive",
        });
        break;
        
      case 429:
        toast({
          title: "Too Many Requests",
          description: "Please wait a moment before trying again.",
          variant: "destructive",
        });
        break;
        
      case 500:
      case 502:
      case 503:
        toast({
          title: "Server Error",
          description: "Something went wrong. Please try again later.",
          variant: "destructive",
        });
        break;
        
      default:
        // Only show toast for other error codes
        if (status >= 400) {
          toast({
            title: "Error",
            description: getErrorMessage(data, message || "An unexpected error occurred."),
            variant: "destructive",
          });
        }
    }
    
    return Promise.reject(error);
  }
);

export default api;
